/**
 * Consent Mode v2 + cookie consent state.
 *
 * Single source of truth for "what the visitor allowed". Wraps Google
 * Consent Mode v2 so GA4 / Google Ads / Meta / TikTok pixels load with
 * `denied` defaults and are upgraded to `granted` only after the user
 * makes a choice.
 *
 * Public API:
 *   - bootstrapConsentMode()    → install gtag stub + defaults (call once, ASAP)
 *   - loadConsent()             → returns { necessary, statistics, marketing, version, ts } or null
 *   - saveConsent(choice, ver)  → persist + push gtag('consent', 'update', ...)
 *   - clearConsent()            → wipe stored consent (forces banner re-show)
 *   - onConsentChange(fn)       → subscribe to consent-changed events
 *   - hasConsent(category)      → boolean helper
 *
 * Categories:
 *   - necessary  → always granted (functional cookies, security, session)
 *   - statistics → analytics_storage (GA4, statistics pixels)
 *   - marketing  → ad_storage + ad_user_data + ad_personalization (Meta, TikTok, Google Ads)
 */

const LS_KEY = 'lazo_cookie_consent_v1';
const EVT = 'lazo:consent-changed';

export const CONSENT_CATEGORIES = ['necessary', 'statistics', 'marketing'];

/* ── Storage ──────────────────────────────────────────────────────── */

export function loadConsent() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Validate shape
    if (typeof parsed !== 'object' || parsed === null) return null;
    if (typeof parsed.version !== 'number') return null;
    return {
      necessary: true,
      statistics: !!parsed.statistics,
      marketing: !!parsed.marketing,
      version: parsed.version,
      ts: parsed.ts || 0,
    };
  } catch {
    return null;
  }
}

export function saveConsent({ statistics, marketing }, version) {
  const payload = {
    statistics: !!statistics,
    marketing: !!marketing,
    version: Number(version) || 1,
    ts: Date.now(),
  };
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(payload));
  } catch { /* private mode etc — silently ignore */ }
  applyConsentToGtag(payload);
  try {
    window.dispatchEvent(new CustomEvent(EVT, { detail: payload }));
  } catch { /* CustomEvent unsupported on very old browsers */ }
  return payload;
}

export function clearConsent() {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}

export function hasConsent(category) {
  const c = loadConsent();
  if (!c) return false;
  if (category === 'necessary') return true;
  return !!c[category];
}

export function isConsentStale(currentServerVersion) {
  const c = loadConsent();
  if (!c) return true; // never consented
  return (currentServerVersion || 1) > c.version;
}

export function onConsentChange(fn) {
  const handler = (e) => fn(e.detail);
  window.addEventListener(EVT, handler);
  return () => window.removeEventListener(EVT, handler);
}

/* ── Google Consent Mode v2 ───────────────────────────────────────── */

/**
 * Install the gtag stub + push the default consent state.
 * MUST run before any pixel script is injected so that GA4/Ads see
 * the "denied" defaults and queue events instead of firing them.
 */
export function bootstrapConsentMode() {
  if (typeof window === 'undefined') return;
  if (window.__lazoConsentBootstrapped) return;
  window.__lazoConsentBootstrapped = true;

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };

  // Defaults: deny everything except security_storage.
  // Note: `wait_for_update: 500` makes GA4 buffer events for 500ms while
  // we resolve the user choice (avoids dropping pageviews on cold load).
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted', // necessary
    security_storage: 'granted',
    wait_for_update: 500,
  });

  // If we already have a stored consent, upgrade right away
  const existing = loadConsent();
  if (existing) applyConsentToGtag(existing);
}

function applyConsentToGtag(consent) {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('consent', 'update', {
    analytics_storage: consent.statistics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
  });
}
